import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import luxonPlugin from '@fullcalendar/luxon3';
import {
  AVAILABILITY_PREFERENCES as preferences, MARKETPLACE_TIMEZONE,
  calendarDateTime, buildAvailabilityEvents, calculateDayStatuses,
} from '../../utils/availabilityCalendar';
import '../../styles/components/profile_page/AvailabilityCalendar.css';

const API = 'https://kc7ciizn3k.execute-api.eu-west-2.amazonaws.com/dev';

const AvailabilityCalendar = ({ photographerId }) => {
  const { authFetch, isAuthenticated, accountReady, userAttributes } = useAuth();
  const isOwnProfile = isAuthenticated && accountReady !== false && userAttributes?.sub === photographerId
    && userAttributes?.['custom:userRole'] === 'photographer';
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [actionError, setActionError] = useState('');
  const [notice, setNotice] = useState('');
  const [saving, setSaving] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [currentTitle, setCurrentTitle] = useState('');
  const [currentView, setCurrentView] = useState('dayGridMonth');
  const [range, setRange] = useState({ start: null, end: null });
  const calendarRef = useRef();
  const anchorDate = useRef();
  const events = useMemo(() => buildAvailabilityEvents(entries), [entries]);
  const dayStatuses = useMemo(() => calculateDayStatuses(events, range.start, range.end), [events, range]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setLoadError(false);
    if (!photographerId) return () => controller.abort();
    const load = async () => {
      try {
        const response = await fetch(`${API}/photographers/${encodeURIComponent(photographerId)}/availability`, { signal: controller.signal });
        if (!response.ok) throw new Error('Availability request failed');
        const data = await response.json();
        if (!Array.isArray(data.entries)) throw new Error('Invalid availability response');
        buildAvailabilityEvents(data.entries);
        if (!controller.signal.aborted) setEntries(data.entries);
      } catch {
        if (!controller.signal.aborted) setLoadError(true);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    };
    load();
    return () => controller.abort();
  }, [photographerId, attempt]);

  const handleDatesSet = useCallback((arg) => {
    setCurrentTitle(arg.view.title);
    setCurrentView(arg.view.type);
    anchorDate.current = arg.view.calendar.getDate();
    setRange((previous) => previous.start?.getTime() === arg.start.getTime() && previous.end?.getTime() === arg.end.getTime()
      ? previous : { start: arg.start, end: arg.end });
  }, []);

  const dayCellClassNames = useCallback((arg) => {
    const status = dayStatuses[calendarDateTime(arg.date).toISODate()];
    return status ? [`${status === 'full' ? 'full' : status === 'partial' ? 'partial' : 'free'}-day`] : [];
  }, [dayStatuses]);

  const handleSelect = async (selectionInfo) => {
    if (!isOwnProfile || saving) return;
    const title = window.prompt('Label for unavailable time', 'Unavailable');
    if (title === null) return;
    setSaving(true);
    setActionError('');
    setNotice('');
    try {
      const response = await authFetch(`${API}/me/availability`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'unavailable', title: title.trim() || 'Unavailable',
          start_time: selectionInfo.startStr, end_time: selectionInfo.endStr }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'We could not block that time. Please try again.');
      setNotice('Unavailable time added.');
      setAttempt((value) => value + 1);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSaving(false);
      calendarRef.current?.getApi().unselect();
    }
  };

  const handleEventClick = async ({ event }) => {
    if (!isOwnProfile || saving) return;
    setActionError('');
    if (event.extendedProps.bookingId) {
      setNotice('This time belongs to a booking. Its schedule is managed with the booking.');
      return;
    }
    if (event.extendedProps.derived) {
      setNotice('This buffer follows the booking.');
      return;
    }
    if (!window.confirm(`Remove "${event.title}"?`)) return;
    setSaving(true);
    setNotice('');
    try {
      const response = await authFetch(`${API}/me/availability/${event.extendedProps.availabilityId}`, { method: 'DELETE' });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'We could not remove that block. Please try again.');
      setNotice('Availability block removed.');
      setAttempt((value) => value + 1);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const navigateCalendar = (direction) => calendarRef.current?.getApi()[direction]();
  const changeCalendarView = (view) => calendarRef.current?.getApi().changeView(view);

  if (loading) return <p role="status">Loading availability…</p>;
  if (loadError) return (
    <div className="availability-error" role="alert">
      <p>We could not load availability.</p>
      <button type="button" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
    </div>
  );

  return (
    <div className="availability-calendar" aria-busy={saving}>
      {isOwnProfile && <p className="availability-instructions">UK time · Select a day, then drag across a time range to mark yourself unavailable. Booking times are managed through bookings.</p>}
      {saving && <p role="status">Saving availability…</p>}
      {notice && <p className="availability-notice" role="status">{notice}</p>}
      {actionError && <p className="availability-error" role="alert">{actionError}</p>}
      <div className="availability-toolbar">
        <div className="availability-toolbar__navigation">
          <button
            type="button"
            className="availability-toolbar__iconButton"
            onClick={() => navigateCalendar('prev')}
            aria-label="Previous period"
          >
            ‹
          </button>
          <button
            type="button"
            className="availability-toolbar__iconButton"
            onClick={() => navigateCalendar('next')}
            aria-label="Next period"
          >
            ›
          </button>
          <button
            type="button"
            className="availability-toolbar__today"
            onClick={() => navigateCalendar('today')}
          >
            Today
          </button>
        </div>

        <div className="availability-toolbar__title">{currentTitle}</div>

        {isOwnProfile && (
          <div className="availability-toolbar__views" aria-label="Calendar view">
            <button
              type="button"
              className={currentView === 'dayGridMonth' ? 'is-active' : ''}
              onClick={() => changeCalendarView('dayGridMonth')}
            >
              Month
            </button>
            <button
              type="button"
              className={currentView === 'timeGridWeek' ? 'is-active' : ''}
              onClick={() => changeCalendarView('timeGridWeek')}
            >
              Week
            </button>
            <button
              type="button"
              className={currentView === 'timeGridDay' ? 'is-active' : ''}
              onClick={() => changeCalendarView('timeGridDay')}
            >
              Day
            </button>
          </div>
        )}
      </div>

      <FullCalendar
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, luxonPlugin]}
        timeZone={MARKETPLACE_TIMEZONE}
        initialView={currentView}
        initialDate={anchorDate.current}
        selectable={isOwnProfile && !saving}
        selectMirror={true}
        events={events}
        select={handleSelect}
        eventClick={isOwnProfile ? handleEventClick : undefined}
        selectAllow={(info) => !info.allDay}
        slotMinTime={preferences.workingStart}
        slotMaxTime={preferences.workingEnd}
        ref={calendarRef}
        datesSet={handleDatesSet}
        dayCellClassNames={dayCellClassNames}
        dayMaxEventRows={true}
        dayMaxEvents={1}
        headerToolbar={false}
        dateClick={isOwnProfile ? (info) => info.view.calendar.changeView('timeGridDay', info.dateStr) : undefined}
      />

      <div className="availability-legend" aria-label="Availability key">
        <span className="legend-item">
          <span className="legend-dot free-day-example" aria-hidden="true"></span>
          Free
        </span>
        <span className="legend-item">
          <span className="legend-dot partial-day-example" aria-hidden="true"></span>
          Limited
        </span>
        <span className="legend-item">
          <span className="legend-dot full-day-example" aria-hidden="true"></span>
          Unavailable
        </span>
      </div>
    </div>
  );
};

export default AvailabilityCalendar;
